import { Visit, VisitDocumentationScope } from '@dreryk/egabinet-visit-frontend-api';
import { Disease } from '@dreryk/medical-dictionaries-api';

import { assertUnreachable } from '~libs.typescript';
import { visitPaths } from '~libs.desktop.shared.paths';
import { BackActionLocation } from '~libs.desktop.video-consultation';

export const clockVisitor = (visit: Visit) => <ACCEPT,>(visitor: {
   timer: (data: { beginDate: Date; plannedBeginDate: Date; plannedEndDate: Date }) => ACCEPT;
   stopwatch: (data: { beginDate: Date }) => ACCEPT;
}) => {
   if (visit.reopened) {
      return null;
   }

   if (visit.outOfSchedule) {
      return visitor.stopwatch({
         beginDate: new Date(visit.beginDate),
      });
   }

   return visitor.timer({
      beginDate: new Date(visit.beginDate),
      plannedBeginDate: new Date(visit.plannedBeginDate),
      plannedEndDate: new Date(visit.plannedEndDate),
   });
};

export const findDefaultVisitDisease = (visit: Visit): Disease | null => {
   if (visit.diagnoses && visit.diagnoses.length === 1) {
      const [firstDiagnosis] = visit.diagnoses;
      return {
         icd10Code: firstDiagnosis.icd10Code,
         id: firstDiagnosis.id,
         name: firstDiagnosis.name,
      };
   }

   return null;
};

type EncounterModeVisitor = <ACCEPT>(visitor: {
   visitVideoConsultation: () => ACCEPT;
   visitOnSite: () => ACCEPT;
}) => ACCEPT;

export const encounterModeVisitor = (visit: Visit): EncounterModeVisitor => (visitor) => {
   if (visit.encounterMode === 'VIDEO_CONSULTATION') {
      return visitor.visitVideoConsultation();
   }

   return visitor.visitOnSite();
};

export const getVideoConsultationVisitLocation = (visitId: number): BackActionLocation => {
   return {
      path: visitPaths.getVisitInterviewPath({
         params: {
            visitId: visitId,
         },
      }),
   };
};

type DocumentationScopeVisitor = <ACCEPT>(visitor: { full: () => ACCEPT; prescriptionOnly: () => ACCEPT }) => ACCEPT;

export const documentationScopeVisitor = (documentationScope: VisitDocumentationScope): DocumentationScopeVisitor => (
   visitor
) => {
   switch (documentationScope) {
      case 'FULL':
         return visitor.full();
      case 'PRESCRIPTION_ONLY':
         return visitor.prescriptionOnly();
      default:
         return assertUnreachable(documentationScope);
   }
};
